import React, { useState } from 'react';
import { Property } from '@/types/index';
import { propertyService } from '@services/property.service';

interface AgenceTabProps {
  property: Property;
  onUpdate: (updated: Partial<Property>) => void;
}

interface FormState {
  sourceUrl: string;
  agencyName: string;
  agencyContactPerson: string;
  agencyAddress: string;
  agencyPhone: string;
  agencyEmail: string;
}

function renderBold(text: string): React.ReactNode {
  const parts = text.split(/\*\*(.+?)\*\*/g);
  if (parts.length === 1) return text;
  return parts.map((part, i) => i % 2 === 1 ? <strong key={i}>{part}</strong> : part);
}

function renderDescription(text: string): React.ReactNode {
  const paragraphs = text.split(/\n{2,}/);
  return paragraphs
    .map(para => para.trim())
    .filter(para => para.length > 0)
    .map((para, i) => {
      const lines = para.split('\n');
      return (
        <p key={i} className="leading-relaxed mb-3 last:mb-0">
          {lines.map((line, j) => (
            <span key={j}>
              {renderBold(line)}
              {j < lines.length - 1 && <br />}
            </span>
          ))}
        </p>
      );
    });
}

export const AgenceTab: React.FC<AgenceTabProps> = ({ property, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [form, setForm] = useState<FormState>({
    sourceUrl: property.sourceUrl ?? '',
    agencyName: property.agencyName ?? '',
    agencyContactPerson: property.agencyContactPerson ?? '',
    agencyAddress: property.agencyAddress ?? '',
    agencyPhone: property.agencyPhone ?? '',
    agencyEmail: property.agencyEmail ?? '',
  });

  const handleSave = async () => {
    try {
      setIsSaving(true);
      const payload = {
        sourceUrl: form.sourceUrl || undefined,
        agencyName: form.agencyName || undefined,
        agencyContactPerson: form.agencyContactPerson || undefined,
        agencyAddress: form.agencyAddress || undefined,
        agencyPhone: form.agencyPhone || undefined,
        agencyEmail: form.agencyEmail || undefined,
      };
      await propertyService.update(property.id, payload as any);
      onUpdate(payload);
      setIsEditing(false);
    } catch (err) {
      console.error('Erreur sauvegarde agence:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setForm({
      sourceUrl: property.sourceUrl ?? '',
      agencyName: property.agencyName ?? '',
      agencyContactPerson: property.agencyContactPerson ?? '',
      agencyAddress: property.agencyAddress ?? '',
      agencyPhone: property.agencyPhone ?? '',
      agencyEmail: property.agencyEmail ?? '',
    });
    setIsEditing(false);
  };

  const isEmpty = !property.sourceUrl && !property.agencyName && !property.agencyContactPerson
    && !property.agencyAddress && !property.agencyPhone && !property.agencyEmail;

  return (
    <div className="space-y-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
            Référence de l&apos;annonce originale
          </h3>
          {!isEditing && (
            <button
              onClick={() => setIsEditing(true)}
              className="px-3 py-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-800/40 rounded-lg transition-colors"
            >
              Modifier
            </button>
          )}
        </div>

        {isEditing ? (
          <div className="space-y-4">
            <Field label="URL de l'annonce">
              <input
                type="url"
                value={form.sourceUrl}
                onChange={e => setForm(f => ({ ...f, sourceUrl: e.target.value }))}
                placeholder="https://www.homegate.ch/..."
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </Field>
            <Field label="Nom de l'agence">
              <input
                type="text"
                value={form.agencyName}
                onChange={e => setForm(f => ({ ...f, agencyName: e.target.value }))}
                placeholder="Ex : Immobilier Dupont SA"
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </Field>
            <Field label="Personne de contact">
              <input
                type="text"
                value={form.agencyContactPerson}
                onChange={e => setForm(f => ({ ...f, agencyContactPerson: e.target.value }))}
                placeholder="Ex : Jean Dupont"
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </Field>
            <Field label="Adresse de l'agence">
              <input
                type="text"
                value={form.agencyAddress}
                onChange={e => setForm(f => ({ ...f, agencyAddress: e.target.value }))}
                placeholder="Ex : Rue de la Paix 10, 1000 Lausanne"
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Téléphone">
                <input
                  type="tel"
                  value={form.agencyPhone}
                  onChange={e => setForm(f => ({ ...f, agencyPhone: e.target.value }))}
                  placeholder="Ex : +41 21 000 00 00"
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </Field>
              <Field label="Email">
                <input
                  type="email"
                  value={form.agencyEmail}
                  onChange={e => setForm(f => ({ ...f, agencyEmail: e.target.value }))}
                  placeholder="Ex : contact@agence.ch"
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </Field>
            </div>

            <div className="flex gap-2 pt-2">
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed rounded-lg transition-colors"
              >
                {isSaving ? 'Sauvegarde...' : 'Enregistrer'}
              </button>
              <button
                onClick={handleCancel}
                disabled={isSaving}
                className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
              >
                Annuler
              </button>
            </div>
          </div>
        ) : isEmpty ? (
          <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-4">
            Aucune information d&apos;agence renseignée.{' '}
            <button onClick={() => setIsEditing(true)} className="text-blue-500 hover:underline">
              Ajouter
            </button>
          </p>
        ) : (
          <dl className="space-y-4">
            {property.sourceUrl && (
              <InfoRow label="URL de l'annonce">
                <a
                  href={property.sourceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 dark:text-blue-400 hover:underline break-all text-sm"
                >
                  {property.sourceUrl}
                </a>
              </InfoRow>
            )}
            {property.agencyName && (
              <InfoRow label="Agence">
                <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                  {property.agencyName}
                </span>
              </InfoRow>
            )}
            {property.agencyContactPerson && (
              <InfoRow label="Contact">
                <span className="text-sm text-gray-800 dark:text-gray-200">
                  {property.agencyContactPerson}
                </span>
              </InfoRow>
            )}
            {property.agencyAddress && (
              <InfoRow label="Adresse">
                <span className="text-sm text-gray-800 dark:text-gray-200">
                  {property.agencyAddress}
                </span>
              </InfoRow>
            )}
            {property.agencyPhone && (
              <InfoRow label="Téléphone">
                <a
                  href={`tel:${property.agencyPhone}`}
                  className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                >
                  {property.agencyPhone}
                </a>
              </InfoRow>
            )}
            {property.agencyEmail && (
              <InfoRow label="Email">
                <a
                  href={`mailto:${property.agencyEmail}`}
                  className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                >
                  {property.agencyEmail}
                </a>
              </InfoRow>
            )}
          </dl>
        )}
      </div>

      {property.description && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-4">
            Description originale de l&apos;annonce
          </h3>
          <div className="text-sm text-gray-800 dark:text-gray-200">
            {renderDescription(property.description)}
          </div>
        </div>
      )}
    </div>
  );
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
      {label}
    </label>
    {children}
  </div>
);

const InfoRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-4">
    <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 sm:w-36 shrink-0 pt-0.5">
      {label}
    </dt>
    <dd className="flex-1">{children}</dd>
  </div>
);
